import { FirebaseAuthProvider } from "./FirebaseAuthProvider.js";
import { FirebaseFunctionProvider } from "./FirebaseFunctionProvider.js";
import { FirebaseStorageProvider } from "./FirebaseStorageProvider.js";
import { FirebaseDatabaseProvider } from "./FirebaseDatabaseProvider.js";
import { FirebaseMessagingProvider } from "./FirebaseMessagingProvider.js";
import { initializeApp } from "firebase/app";
//#region src/createFirebaseProvider.ts
/**
* Create a Firebase service provider
*
* @example
* ```typescript
* import { services } from "@progressive-development/pd-spa-helper";
* import { createFirebaseProvider } from "@progressive-development/pd-provider-firebase";
*
* const provider = createFirebaseProvider({
*   firebaseConfig: {
*     apiKey: "...",
*     authDomain: "...",
*     projectId: "...",
*   },
*   functionsRegion: "europe-west3",
*   enableDatabase: true,
* });
*
* services.initialize(provider);
* ```
*/
/**
* Builds one provider per named database.
*
* Deliberately independent of `enableDatabase`: That flag decides whether the
* app uses a database at all in the classic sense; named databases are a
* separate, explicit list. An app can read from a shared database without
* having a primary one.
*/
function benannteDatenbanken(app, namen) {
	if (!namen || Object.keys(namen).length === 0) return;
	const providers = {};
	for (const [name, databaseId] of Object.entries(namen)) providers[name] = new FirebaseDatabaseProvider(app, databaseId);
	return providers;
}
function createFirebaseProvider(config) {
	const app = initializeApp(config.firebaseConfig);
	return {
		auth: new FirebaseAuthProvider(app),
		functions: new FirebaseFunctionProvider(app, {
			region: config.functionsRegion,
			emulator: config.functionsEmulator
		}),
		storage: new FirebaseStorageProvider(app, config.storageConfig),
		database: config.enableDatabase ? new FirebaseDatabaseProvider(app, config.databaseId) : void 0,
		databases: benannteDatenbanken(app, config.databases),
		messaging: config.messagingConfig ? new FirebaseMessagingProvider(app, config.messagingConfig) : void 0,
		getFirebaseApp: () => app
	};
}
/**
* Create a Firebase provider with an existing Firebase App instance
*
* Useful when the app is already initialized elsewhere
*/
function createFirebaseProviderFromApp(app, options) {
	return {
		auth: new FirebaseAuthProvider(app),
		functions: new FirebaseFunctionProvider(app, {
			region: options?.functionsRegion,
			emulator: options?.functionsEmulator
		}),
		storage: new FirebaseStorageProvider(app, options?.storageConfig),
		database: options?.enableDatabase ? new FirebaseDatabaseProvider(app, options.databaseId) : void 0,
		databases: benannteDatenbanken(app, options?.databases),
		messaging: options?.messagingConfig ? new FirebaseMessagingProvider(app, options.messagingConfig) : void 0,
		getFirebaseApp: () => app
	};
}
//#endregion
export { createFirebaseProvider, createFirebaseProviderFromApp };
